import {AutomationNode, NodeConnection, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {getNodeOutputs} from './nodeOutputs';
import {guaranteedUpstreamNodes} from './reachability';

/**
 * SEND_TEAM_EMAIL, mirrored by the API's go-live check: it writes to the team member its run is for, so a Team
 * members step has to come before it, with no Wait for everyone in between (that ends the member runs).
 */

/** Ancestors nearest first (breadth-first, like `upstreamNodeData`), not walking on past a node `stopAt` accepts. */
const ancestors = (
    nodeId: string,
    nodes: AutomationNode[],
    connections: NodeConnection[],
    stopAt?: (node: AutomationNode) => boolean
): AutomationNode[] => {
    const byId = new Map(nodes.map(node => [node.id, node]));
    const seen = new Set<string>([nodeId]);
    const ordered: AutomationNode[] = [];
    let frontier = [nodeId];

    while (frontier.length) {
        const next: string[] = [];

        frontier.forEach(targetId => connections
            .filter(connection => connection.targetNodeId === targetId && !seen.has(connection.sourceNodeId))
            .forEach(connection => {
                seen.add(connection.sourceNodeId);
                const node = byId.get(connection.sourceNodeId);
                if (!node) return;

                ordered.push(node);
                if (!stopAt?.(node)) next.push(node.id);
            }));

        frontier = next;
    }

    return ordered;
};

/** The Team members step whose member runs reach `nodeId`, nearest first; undefined when none does. */
export const teamMembersStepAbove = (
    nodeId: string,
    nodes: AutomationNode[],
    connections: NodeConnection[]
): AutomationNode | undefined => ancestors(
    nodeId,
    nodes,
    connections,
    node => node.type === NodeType.TEAM_MEMBERS || node.type === NodeType.AFTER_EVERYONE
).find(node => node.type === NodeType.TEAM_MEMBERS);

export const readAttachmentSourceNodeIds = (config?: Record<string, any>): string[] =>
    Array.isArray(config?.attachmentSourceNodeIds) ? config!.attachmentSourceNodeIds.filter((id: unknown): id is string => typeof id === 'string') : [];

/** File producers must execute on every path to the sender. */
export const attachmentSourceSteps = (
    upstream: AutomationNode[], nodes: AutomationNode[] = [], connections: NodeConnection[] = [], nodeId?: string
): AutomationNode[] => guaranteedUpstreamNodes(upstream, nodes, connections, nodeId)
    .filter(node => getNodeOutputs(node.type).some(output => output.name === 'artifactIds' && output.type === 'array'));

export const remapAttachmentSourceNodeIds = (config: Record<string, any>, ids: ReadonlyMap<string, string>): Record<string, any> =>
    Array.isArray(config.attachmentSourceNodeIds)
        ? {...config, attachmentSourceNodeIds: config.attachmentSourceNodeIds.map((id: string) => ids.get(id) ?? id)}
        : config;

/** A Send team email step no Team members step's member runs reach: nobody to write to. */
export const teamEmailWithoutTeam = (
    node: AutomationNode,
    nodes: AutomationNode[],
    connections: NodeConnection[]
): boolean => node.type === NodeType.SEND_TEAM_EMAIL && !teamMembersStepAbove(node.id, nodes, connections);

export const teamEmailWithInvalidAttachments = (
    node: AutomationNode, nodes: AutomationNode[], connections: NodeConnection[]
): boolean => {
    if (node.type !== NodeType.SEND_TEAM_EMAIL) return false;
    const stored = node.config?.attachmentSourceNodeIds;
    if (stored !== undefined && (!Array.isArray(stored) || stored.some(id => typeof id !== 'string' || !id.trim())
        || new Set(stored).size !== stored.length)) return true;
    const sources = attachmentSourceSteps(ancestors(node.id, nodes, connections), nodes, connections, node.id);
    return readAttachmentSourceNodeIds(node.config).some(id => !sources.some(source => source.id === id));
};

export const teamEmailWithoutContent = (node: AutomationNode): boolean => node.type === NodeType.SEND_TEAM_EMAIL
    && (node.config?.emailMode !== 'BUILDER' || !String(node.config?.subject ?? '').trim() || !String(node.config?.content ?? '').trim());
